import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Play } from "lucide-react";

export default function Login() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && user) navigate("/dashboard", { replace: true });
  }, [user, loading, navigate]);

  const handleGoogleSignIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen relative flex items-stretch bg-zinc-950 grain overflow-hidden">
      {/* Left panel: brand */}
      <div className="hidden lg:flex flex-col justify-between p-14 w-1/2 relative z-10">
        <div
          aria-hidden="true"
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage:
              'url("https://images.unsplash.com/photo-1709377195538-5522ed0f9e10?crop=entropy&cs=srgb&fm=jpg&w=1400&q=70")',
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-zinc-950/70 via-zinc-950/40 to-zinc-950" />
        <div className="relative">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-md bg-white text-zinc-950 flex items-center justify-center">
              <Play className="h-5 w-5 fill-current" />
            </div>
            <span className="font-display text-xl font-bold tracking-tight">PURNOVA OPS</span>
          </div>
        </div>
        <div className="relative">
          <h1 className="font-display text-5xl xl:text-6xl font-bold leading-[1.05] max-w-lg">
            Branding That Lasts. Growth That Matters.
          </h1>
          <p className="mt-6 text-zinc-400 max-w-md">
            Client rosters, shoot timelines, editor workloads, and approval queues —
            one control room for your agency.
          </p>
          <div className="mt-10 grid grid-cols-3 gap-4 max-w-md text-xs">
            {[
              { k: "Active", v: "Clients" },
              { k: "Deliverables", v: "This Month" },
              { k: "In-Review", v: "Approvals" },
            ].map((it, i) => (
              <div key={i} className="rounded-lg border border-white/5 p-3 bg-zinc-950/60 backdrop-blur">
                <div className="font-mono text-zinc-500 uppercase tracking-widest text-[10px]">{it.k}</div>
                <div className="mt-1 text-zinc-200">{it.v}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right panel: sign-in */}
      <div className="flex-1 flex items-center justify-center p-8 relative z-10">
        <div className="w-full max-w-sm">
          <div className="lg:hidden mb-10 flex items-center gap-3">
            <div className="h-9 w-9 rounded-md bg-white text-zinc-950 flex items-center justify-center">
              <Play className="h-4 w-4 fill-current" />
            </div>
            <span className="font-display font-bold tracking-tight">Purnova Ops</span>
          </div>
          <div className="text-xs font-mono uppercase tracking-widest text-zinc-500">01 / Sign In</div>
          <h2 className="mt-2 font-display text-4xl font-bold">Enter Purnova's Space</h2>
          <p className="mt-3 text-zinc-400 text-sm">
            The owner account is <span className="text-zinc-200 font-mono">handerushikesh14@gmail.com</span>.
            Team members are onboarded automatically.
          </p>

          <button
            data-testid="google-signin-button"
            onClick={handleGoogleSignIn}
            className="mt-10 w-full group flex items-center justify-center gap-3 rounded-full bg-white text-zinc-950 px-5 py-3.5 font-semibold hover:bg-zinc-200 transition-colors"
          >
            <svg className="h-5 w-5" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.1c-.22-.66-.35-1.36-.35-2.1s.13-1.44.35-2.1V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l3.66-2.84z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/>
            </svg>
            Continue with Google
          </button>

          <div className="mt-8 pt-8 border-t border-white/5 text-xs text-zinc-500">
            By continuing, you accept our internal operations policy and Drive access terms.
          </div>
        </div>
      </div>
    </div>
  );
}
